import React, { useState, useEffect, useRef } from "react";
import { documentMapAPI } from "../services/api";
import { Map, RefreshCw, Loader2, Info } from "lucide-react";

const COLORS = [
  "#14b88e", "#6378b3", "#fbbf24", "#f87171",
  "#a78bfa", "#34d399", "#fb923c", "#60a5fa",
  "#f472b6", "#4ade80",
];

function toPixel(val, min, max, size, padding) {
  if (max === min) return size / 2;
  return padding + ((val - min) / (max - min)) * (size - padding * 2);
}

export default function DocumentMapPage({ documents }) {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  const fetchMap = async () => {
    setLoading(true);
    setError(null);
    setMapData(null);
    try {
      const res = await documentMapAPI.get();
      if (res.data.error) setError(res.data.error);
      else setMapData(res.data);
    } catch (e) {
      setError("Failed to generate map. Is the backend running?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (documents.length > 0) fetchMap();
  }, [documents.length]);

  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <Map size={40} className="text-ink-700 mb-4" />
        <h3 className="font-display text-xl font-bold text-white mb-2">Document Map</h3>
        <p className="text-ink-500 text-sm">Upload documents to visualize their semantic structure.</p>
      </div>
    );
  }

  const W = 900, H = 580, PAD = 60;
  let points = [], clusters = [];

  if (mapData?.points?.length > 0) {
    const xs = mapData.points.map(p => p.x);
    const ys = mapData.points.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);

    points = mapData.points.map(p => ({
      ...p,
      px: toPixel(p.x, minX, maxX, W, PAD),
      py: toPixel(p.y, minY, maxY, H, PAD),
    }));

    clusters = (mapData.clusters || []).map(c => ({
      ...c,
      px: toPixel(c.x, minX, maxX, W, PAD),
      py: toPixel(c.y, minY, maxY, H, PAD),
    }));
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-6 py-4 border-b border-ink-800 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-white flex items-center gap-2">
            <Map size={18} className="text-arc-400" />
            Document Map
          </h2>
          <p className="text-ink-500 text-xs mt-0.5">
            Semantic clusters auto-labeled by AI — hover dots to preview content
          </p>
        </div>
        <button onClick={fetchMap} disabled={loading} className="btn-ghost text-xs flex items-center gap-1.5">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="flex-1 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-ink-950/90 z-20">
            <Loader2 size={28} className="text-arc-400 animate-spin" />
            <p className="text-ink-400 text-sm">Computing embeddings & generating cluster labels...</p>
            <p className="text-ink-600 text-xs">This may take 15–30 seconds</p>
          </div>
        )}

        {error && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Info size={28} className="text-amber-400" />
            <p className="text-ink-400 text-sm text-center max-w-sm">{error}</p>
          </div>
        )}

        {mapData && !loading && points.length > 0 && (
          <div className="w-full h-full relative">
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} className="w-full h-full" style={{overflow:"hidden"}}>
              {/* Cluster blobs */}
              {clusters.map((cluster) => {
                const clusterPts = points.filter(p => p.cluster === cluster.id);
                if (clusterPts.length < 2) return null;
                const dists = clusterPts.map(p =>
                  Math.sqrt((p.px - cluster.px) ** 2 + (p.py - cluster.py) ** 2)
                );
                const r = Math.max(30, Math.min(90, Math.max(...dists) * 0.85 + 18));
                const color = COLORS[cluster.id % COLORS.length];
                return (
                  <circle key={`blob-${cluster.id}`}
                    cx={cluster.px} cy={cluster.py} r={r}
                    fill={color} fillOpacity={0.07}
                    stroke={color} strokeOpacity={0.2}
                    strokeWidth={1.5} strokeDasharray="4 3"
                  />
                );
              })}

              {/* Data points */}
              {points.map((p) => {
                const color = COLORS[p.cluster % COLORS.length];
                const isHovered = hoveredPoint?.id === p.id;
                return (
                  <circle key={p.id}
                    cx={p.px} cy={p.py}
                    r={isHovered ? 8 : 5}
                    fill={color} fillOpacity={isHovered ? 1 : 0.85}
                    stroke={isHovered ? "#fff" : color}
                    strokeWidth={isHovered ? 2 : 0.5} strokeOpacity={0.4}
                    style={{ cursor: "pointer", transition: "r 0.15s" }}
                    onMouseEnter={(e) => {
                      setHoveredPoint(p);
                      const rect = svgRef.current?.getBoundingClientRect();
                      if (rect) setTooltip({ x: e.clientX - rect.left, y: e.clientY - rect.top, point: p });
                    }}
                    onMouseLeave={() => { setHoveredPoint(null); setTooltip(null); }}
                  />
                );
              })}

              {/* Cluster label pills */}
              {clusters.map((cluster) => {
                const color = COLORS[cluster.id % COLORS.length];
                const label = cluster.label || `Topic ${cluster.id + 1}`;
                const pillW = Math.max(80, label.length * 7.5 + 24);
                const pillH = 24;
                const clusterPts = points.filter(p => p.cluster === cluster.id);
                const minPY = clusterPts.length > 0 ? Math.min(...clusterPts.map(p => p.py)) : cluster.py;
                const labelY = Math.max(30, minPY - 28);
                return (
                  <g key={`label-${cluster.id}`}>
                    <rect
                      x={cluster.px - pillW / 2} y={labelY - pillH / 2}
                      width={pillW} height={pillH} rx={pillH / 2}
                      fill={color} fillOpacity={0.18}
                      stroke={color} strokeOpacity={0.55} strokeWidth={1}
                    />
                    <text x={cluster.px} y={labelY + 1}
                      textAnchor="middle" dominantBaseline="middle"
                      fill={color} fontSize={11}
                      fontFamily="'DM Sans', sans-serif" fontWeight="600"
                      style={{ pointerEvents: "none", userSelect: "none" }}>
                      {label}
                    </text>
                    <text x={cluster.px} y={labelY + pillH / 2 + 10}
                      textAnchor="middle" fill={color} fillOpacity={0.6}
                      fontSize={9} fontFamily="'DM Sans', sans-serif"
                      style={{ pointerEvents: "none", userSelect: "none" }}>
                      {cluster.count} chunk{cluster.count !== 1 ? "s" : ""}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* Hover tooltip */}
            {tooltip && (
              <div className="absolute z-30 pointer-events-none"
                style={{ left: Math.min(tooltip.x + 12, W - 260), top: Math.max(tooltip.y - 80, 8) }}>
                <div className="bg-ink-800 border border-ink-600 rounded-xl p-3 w-56 shadow-xl">
                  <p className="text-xs font-semibold text-arc-300 mb-1 truncate">{tooltip.point.doc_name}</p>
                  <p className="text-xs text-ink-300 leading-relaxed line-clamp-4">{tooltip.point.text}</p>
                  <p className="text-xs text-ink-600 mt-1.5">
                    Chunk #{tooltip.point.chunk_index} ·{" "}
                    <span style={{ color: COLORS[tooltip.point.cluster % COLORS.length] }}>
                      {mapData.clusters?.find(c => c.id === tooltip.point.cluster)?.label || `Cluster ${tooltip.point.cluster}`}
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="absolute bottom-4 right-4 bg-ink-900/80 border border-ink-700 rounded-xl p-3 backdrop-blur-sm">
              <p className="text-xs font-semibold text-ink-500 uppercase tracking-wider mb-2">
                {clusters.length} Clusters · {points.length} Chunks
              </p>
              <div className="space-y-1.5">
                {clusters.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: COLORS[c.id % COLORS.length] }} />
                    <span className="text-xs text-ink-300 truncate max-w-36">
                      {c.label || `Topic ${c.id + 1}`}
                    </span>
                    <span className="text-xs text-ink-600 ml-auto">{c.count}</span>
                  </div>
                ))}
              </div>
              {mapData.method === "pca" && (
                <p className="text-xs text-amber-400/70 mt-2 border-t border-ink-700 pt-2">Using PCA projection</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}